import type { MeasurementPrefUtility } from '../helpers/MeasurementPref';
import { ascentProfile, CLIMBING_GRADIENT_PERCENT, type AscentProfile } from './ascent';
import {
  cadenceScale,
  CLIMBING_SPEED_COLORS,
  DESCENDING_SPEED_COLORS,
  DOWNHILL_COLOR,
  ELEVATION_COLOR,
  heartRateScale,
  LEVEL_COLOR,
  MINIMUM_CADENCE_SPREAD_STEPS_PER_MINUTE,
  MINIMUM_ELEVATION_SPREAD_METERS,
  MINIMUM_HEART_RATE_SPREAD_BPM,
  MINIMUM_PACE_SPREAD_SECONDS_PER_METER,
  paceScale,
  rampColor,
  speedScale,
  UPHILL_COLOR,
  type ColorScale,
  type SpeedScale,
} from './colorScale';
import {
  durationLabel,
  hoursAndMinutes,
  minutesSeconds,
  paceUnit,
  paceValue,
  secondsPerBigUnit,
  smallUnitValue,
  strideValue,
  toSmallUnit,
  verticalSpeedUnit,
  verticalSpeedValue,
  wholeNumber,
  withUnit,
} from './format';
import { ageOn, zoneTimes, type SecondsByBpm, type ZoneTimes } from './heartRateZones';
import {
  cadenceProfile,
  elapsedMsAt,
  elevationProfile,
  heartRateProfile,
  paceProfile,
  type CadenceProfile,
  type PaceProfile,
  type ProfileSample,
  type TimedReading,
} from './profiles';
import { movingElapsedMs, valueAt } from './timeline';
import { highPointIndex } from '../elevation/highPoint';

export interface ElevationFigures {
  gainMeters: number;
  lossMeters: number;
  highestMeters: number;
  lowestMeters: number;
}

export interface HeartRateSummary {
  average: number;
  max: number;
  min: number;
}

/**
 * What the recording page publishes on `#recording_activity`. A long recording's track and readings
 * are thinned, so they only draw the lines; the figures come worked out from every reading.
 */
export interface RecordingActivityInput {
  timeStamps?: number[] | null;
  distances?: number[] | null;
  altitudes?: Array<number | null> | null;
  movingSeconds?: number | null;
  segmentStartIndices?: number[] | null;
  elevation?: ElevationFigures | null;
  heartRateSamples?: Array<[number, number]> | null;
  heartRateSummary?: HeartRateSummary | null;
  /** Only on the viewer's own recording: zones are personal. */
  zones?: { dateOfBirth: string; secondsByBpm: SecondsByBpm } | null;
  stepSamples?: Array<[number, number]> | null;
}

export type ActivityTab = 'elevation' | 'ascent' | 'heartRate' | 'steps' | 'pace';

export interface ElevationActivity extends ElevationFigures {
  samples: ProfileSample[];
}

export interface HeartRateActivity {
  summary: HeartRateSummary;
  /** Null when the readings can't be placed on the track. */
  samples: ProfileSample[] | null;
  zones: ZoneTimes | null;
}

export interface RecordingActivity {
  totalMeters: number;
  elevation: ElevationActivity | null;
  ascent: AscentProfile | null;
  heartRate: HeartRateActivity | null;
  steps: CadenceProfile | null;
  pace: PaceProfile | null;
  /** Moving time at a distance along the track; null without times. */
  elapsedMsAt: (meters: number) => number | null;
}

type Translate = (key: string, params?: Record<string, unknown>) => string;

function readingsOf(samples: Array<[number, number]> | null | undefined): TimedReading[] {
  return (samples ?? [])
    .filter((s) => Array.isArray(s) && Number.isFinite(s[0]) && Number.isFinite(s[1]))
    .map(([timestampMs, value]) => ({ timestampMs, value }));
}


/** Null when there's nothing to show, so the page leaves the section out. */
export function recordingActivity(input: RecordingActivityInput, today = new Date()): RecordingActivity | null {
  const distances = input.distances ?? [];
  if (distances.length < 2 || !(distances[distances.length - 1] > 0)) return null;

  const timeStampsMs = input.timeStamps ?? [];
  const elapsedMs = movingElapsedMs(timeStampsMs, distances.length, input.movingSeconds, input.segmentStartIndices ?? []);
  const track = elapsedMs ? { distances, timeStampsMs, elapsedMs } : null;

  const elevation = input.elevation && elevationProfile(distances, input.altitudes ?? []);
  const summary = input.heartRateSummary;
  const zones = input.zones;

  const activity: RecordingActivity = {
    totalMeters: distances[distances.length - 1],
    elevation: elevation ? { ...input.elevation!, samples: elevation } : null,
    ascent: elevation
      ? ascentProfile(distances, input.altitudes ?? [], elapsedMs, input.segmentStartIndices ?? [])
      : null,
    heartRate: summary
      ? {
          summary,
          samples: track && heartRateProfile(readingsOf(input.heartRateSamples), track),
          zones: zones ? zoneTimes(zones.secondsByBpm, ageOn(zones.dateOfBirth, today), summary.max) : null,
        }
      : null,
    steps: track && cadenceProfile(readingsOf(input.stepSamples), track),
    pace: track && paceProfile(track),
    elapsedMsAt: (meters) => (track ? elapsedMsAt(meters, track) : null),
  };
  return activityTabs(activity).length > 0 ? activity : null;
}

export function activityTabs(activity: RecordingActivity): ActivityTab[] {
  const tabs: ActivityTab[] = [];
  if (activity.elevation) tabs.push('elevation');
  if (activity.ascent) tabs.push('ascent');
  if (activity.heartRate) tabs.push('heartRate');
  if (activity.steps) tabs.push('steps');
  if (activity.pace) tabs.push('pace');
  return tabs;
}

/** Heart rate first when there is any, as it's what the section is for. */
export function defaultActivityTab(tabs: ActivityTab[]): ActivityTab {
  return tabs.includes('heartRate') ? 'heartRate' : tabs[0];
}

export interface ActivityFigure {
  label: string;
  value: string;
  unit: string;
  /** Another number and unit before the value, as a duration's hours are in "4 h 12 mins". */
  leading?: { value: string; unit: string };
}

/** "4 h 12 mins", "3 h" or "12 mins": whole hours and minutes, as the apps show a duration. */
export function durationFigure(label: string, seconds: number, t: Translate): ActivityFigure {
  const { hours, minutes } = hoursAndMinutes(seconds);
  const hoursUnit = t('recordings.activity.hours_unit');
  const minutesUnit = minutes === 1 ? t('recordings.activity.minute_unit') : t('recordings.activity.minutes_unit');
  if (hours === 0) return { label, value: String(minutes), unit: minutesUnit };
  if (minutes === 0) return { label, value: String(hours), unit: hoursUnit };
  return { label, value: String(minutes), unit: minutesUnit, leading: { value: String(hours), unit: hoursUnit } };
}

export interface ActivityCard {
  tab: ActivityTab;
  title: string;
  value: string;
  unit: string;
}

export function activityCard(
  tab: ActivityTab,
  activity: RecordingActivity,
  pref: MeasurementPrefUtility,
  t: Translate,
  locale?: string
): ActivityCard {
  switch (tab) {
    case 'elevation':
      return {
        tab,
        title: t('recordings.activity.elevation'),
        value: smallUnitValue(activity.elevation!.gainMeters, pref, locale),
        unit: t('recordings.activity.unit_gain', { unit: pref.meterSymbol() }),
      };
    case 'ascent': {
      const { climbing, maxGradient } = activity.ascent!;
      const title = t('recordings.activity.ascent');
      return climbing
        ? {
            tab,
            title,
            value: verticalSpeedValue(climbing.metersPerHour, pref, locale),
            unit: t('recordings.activity.unit_avg_climbing', { unit: verticalSpeedUnit(pref) }),
          }
        : { tab, title, value: String(Math.round(maxGradient)), unit: t('recordings.activity.unit_max_gradient', { unit: '%' }) };
    }
    case 'heartRate':
      return {
        tab,
        title: t('recordings.activity.heart_rate'),
        value: String(activity.heartRate!.summary.average),
        unit: t('recordings.activity.unit_average', { unit: t('recordings.activity.bpm') }),
      };
    case 'steps':
      return {
        tab,
        title: t('recordings.activity.steps'),
        value: wholeNumber(activity.steps!.totalSteps, locale),
        unit: t('recordings.activity.steps_unit'),
      };
    case 'pace':
      return {
        tab,
        title: t('recordings.activity.pace'),
        value: paceValue(activity.pace!.averageSecondsPerMeter, pref),
        unit: t('recordings.activity.unit_average', { unit: paceUnit(pref) }),
      };
  }
}

export function activityFigures(
  tab: ActivityTab,
  activity: RecordingActivity,
  pref: MeasurementPrefUtility,
  t: Translate,
  locale?: string
): ActivityFigure[] {
  const height = (meters: number) => smallUnitValue(meters, pref, locale);
  switch (tab) {
    case 'elevation': {
      const { gainMeters, lossMeters, highestMeters, lowestMeters } = activity.elevation!;
      const unit = pref.meterSymbol();
      return [
        { label: t('recordings.activity.gain'), value: height(gainMeters), unit },
        { label: t('recordings.activity.loss'), value: height(lossMeters), unit },
        { label: t('recordings.activity.max'), value: height(highestMeters), unit },
        { label: t('recordings.activity.min'), value: height(lowestMeters), unit },
      ];
    }
    case 'ascent': {
      const { climbing, descending, maxGradient } = activity.ascent!;
      const gradient = { label: t('recordings.activity.max_gradient'), value: String(Math.round(maxGradient)), unit: '%' };
      if (!climbing) {
        const unit = pref.meterSymbol();
        return [
          gradient,
          { label: t('recordings.activity.gain'), value: height(activity.elevation!.gainMeters), unit },
          { label: t('recordings.activity.loss'), value: height(activity.elevation!.lossMeters), unit },
        ];
      }
      const speed = (metersPerHour: number) => verticalSpeedValue(metersPerHour, pref, locale);
      const unit = verticalSpeedUnit(pref);
      return [
        { label: t('recordings.activity.ascent_rate'), value: speed(climbing.metersPerHour), unit },
        ...(descending ? [{ label: t('recordings.activity.descent_rate'), value: speed(descending.metersPerHour), unit }] : []),
        gradient,
        durationFigure(t('recordings.activity.time_climbing'), climbing.seconds, t),
      ];
    }
    case 'heartRate': {
      const { average, max, min } = activity.heartRate!.summary;
      const unit = t('recordings.activity.bpm');
      return [
        { label: t('recordings.activity.average'), value: String(average), unit },
        { label: t('recordings.activity.max'), value: String(max), unit },
        { label: t('recordings.activity.min'), value: String(min), unit },
      ];
    }
    case 'steps': {
      const { totalSteps, averageStepsPerMinute, averageStrideMeters } = activity.steps!;
      return [
        { label: t('recordings.activity.total'), value: wholeNumber(totalSteps, locale), unit: t('recordings.activity.steps_unit') },
        { label: t('recordings.activity.cadence'), value: wholeNumber(averageStepsPerMinute, locale), unit: t('recordings.activity.spm') },
        { label: t('recordings.activity.stride'), value: strideValue(averageStrideMeters, pref, locale), unit: pref.meterSymbol() },
      ];
    }
    case 'pace': {
      const { averageSecondsPerMeter, fastestSecondsPerMeter } = activity.pace!;
      const unit = paceUnit(pref);
      return [
        { label: t('recordings.activity.average'), value: paceValue(averageSecondsPerMeter, pref), unit },
        { label: t('recordings.activity.fastest'), value: paceValue(fastestSecondsPerMeter, pref), unit },
      ];
    }
  }
}

export interface ChartPoint {
  x: number;
  y: number;
  /** Ascent's metres an hour, negative going down. */
  speed?: number | null;
}

export interface AscentSpeedScales {
  climbing: SpeedScale | null;
  descending: SpeedScale | null;
}

/** What the chart draws for a tab, already in the user's units: x in km or mi. */
export interface ActivitySeries {
  points: ChartPoint[];
  /** A gradient keyed to the y values, one colour, or a colour at each point along the track. */
  colors: ColorScale | { color: string } | { stops: Array<{ x: number; color: string }> };
  /** Pace reads faster upwards. */
  reverse: boolean;
  /** Set when the readings span less than the graph's minimum, so a flat walk stays level. */
  yMin?: number;
  yMax?: number;
  /** Round ticks for elevation and pace; with them yMin and yMax are the axis itself. */
  yTicks?: number[];
  formatValue: (y: number) => string;
  unit: string;
  /** Names the reading in the tooltip, where a bare "12%" would be ambiguous. */
  valueLabel?: string;
  /** Drawn either side of a 0 line rather than up from the bottom, as gradient is. */
  baseline?: boolean;
  /** What ascent's colours stand for, for its legend. */
  speedScales?: AscentSpeedScales | null;
  /** The track's length in the big unit: every tab's axis runs the whole of it. */
  xMax: number;
  /** Round distances from 0, all in the big unit. */
  xTicks: number[];
  formatX: (x: number) => string;
  /** The elevation profile in the user's small unit, drawn behind for context on its own scale. */
  backdrop: ChartPoint[] | null;
}

function yRange(values: number[], minimumSpread: number): { yMin?: number; yMax?: number } {
  const lowest = Math.min(...values);
  const highest = Math.max(...values);
  if (highest - lowest >= minimumSpread) return {};
  const yMin = (lowest + highest - minimumSpread) / 2;
  return { yMin, yMax: yMin + minimumSpread };
}

const PACE_STEPS_SECONDS = [15, 30, 60, 120, 300, 600, 900, 1200, 1800];
const ELEVATION_STEPS = [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000];
const DISTANCE_STEPS = [0.1, 0.2, 0.5, 1, 2, 5, 10, 20, 50, 100, 200, 500, 1000];

/** The step's multiples from `from` to `to`, rid of floating-point dust such as 0.30000000000000004. */
function multiples(from: number, to: number, step: number): number[] {
  const ticks: number[] = [];
  for (let i = Math.ceil(from / step - 1e-9); i * step <= to + step * 1e-9; i++) ticks.push(Number((i * step).toFixed(6)));
  return ticks;
}

function firstStep(steps: number[], fits: (step: number) => boolean): number {
  return steps.find(fits) ?? steps[steps.length - 1];
}

/** Whole or half minutes inside the readings: rounding pace out to nice ticks would put 0:00 /km on it. */
export function paceTicks(lowest: number, highest: number): number[] {
  return multiples(lowest, highest, firstStep(PACE_STEPS_SECONDS, (step) => (highest - lowest) / step <= 3));
}

function enclosingTicks(lowest: number, highest: number, steps: number[], maxGaps: number): number[] {
  const step = firstStep(steps, (s) => Math.ceil(highest / s) - Math.floor(lowest / s) <= maxGaps);
  return multiples(Math.floor(lowest / step) * step, Math.ceil(highest / step) * step, step);
}

/** Round heights enclosing the readings, in at most four gaps. */
export function elevationTicks(lowest: number, highest: number): number[] {
  return enclosingTicks(lowest, highest, ELEVATION_STEPS, 4);
}

const GRADIENT_STEPS = [1, 2, 5, 10, 20, 50];

/** Round percentages either side of 0, at least ±5% so a flat walk stays level. */
export function gradientTicks(lowest: number, highest: number): number[] {
  return enclosingTicks(Math.min(lowest, -5), Math.max(highest, 5), GRADIENT_STEPS, 5);
}

/** 0, 0.5, 1.0, 1.5: round distances up to `length`, in at most six gaps, to the step's one decimal. */
export function distanceTicks(length: number): { ticks: number[]; format: (x: number) => string } {
  const step = firstStep(DISTANCE_STEPS, (s) => length / s <= 6);
  return {
    ticks: multiples(0, length, step),
    format: (x) => (x === 0 ? '0' : x.toFixed(step < 1 ? 1 : 0)),
  };
}

/** Neighbouring readings averaged together, so the line reads as a trend rather than jitter. */
export const SMOOTHING_WINDOW = 5;

export function smoothed(points: ChartPoint[]): ChartPoint[] {
  const reach = Math.floor(SMOOTHING_WINDOW / 2);
  return points.map((point, i) => {
    const window = points.slice(Math.max(i - reach, 0), Math.min(i + reach, points.length - 1) + 1);
    return { x: point.x, y: window.reduce((total, p) => total + p.y, 0) / window.length };
  });
}

function ascentSpeedScales(points: ChartPoint[]): AscentSpeedScales | null {
  if (points.every((p) => p.speed == null)) return null;
  const climbing = points.filter((p) => p.y >= CLIMBING_GRADIENT_PERCENT && p.speed != null && p.speed > 0).map((p) => p.speed!);
  const descending = points.filter((p) => p.y <= -CLIMBING_GRADIENT_PERCENT && p.speed != null && p.speed < 0).map((p) => -p.speed!);
  return { climbing: speedScale(climbing), descending: speedScale(descending) };
}

/** Pale when height came slowly, deep when it came fast; by the slope alone without times. */
export function ascentColor(point: ChartPoint, scales: AscentSpeedScales | null): string {
  if (Math.abs(point.y) < CLIMBING_GRADIENT_PERCENT) return LEVEL_COLOR;
  const up = point.y > 0;
  const scale = up ? scales?.climbing : scales?.descending;
  if (!scale || point.speed == null) return up ? UPHILL_COLOR : DOWNHILL_COLOR;
  const fraction = (Math.abs(point.speed) - scale.low) / (scale.high - scale.low);
  return rampColor(up ? CLIMBING_SPEED_COLORS : DESCENDING_SPEED_COLORS, fraction);
}

/** As `smoothed`, over the readings there are; a missing one stays missing. */
export function smoothedReadings(values: Array<number | null>): Array<number | null> {
  const reach = Math.floor(SMOOTHING_WINDOW / 2);
  return values.map((value, i) => {
    if (value === null) return null;
    const window = values.slice(Math.max(i - reach, 0), i + reach + 1).filter((v): v is number => v !== null);
    return window.reduce((total, v) => total + v, 0) / window.length;
  });
}

function scaled(scale: ColorScale, factor: number): ColorScale {
  return { ...scale, low: scale.low * factor, high: scale.high * factor };
}

export function activitySeries(
  tab: ActivityTab,
  activity: RecordingActivity,
  pref: MeasurementPrefUtility,
  t: Translate
): ActivitySeries | null {
  const series = tabSeries(tab, activity, pref, t);
  if (!series) return null;
  const xMax = activity.totalMeters / pref.metresFromBigUnit(1);
  const { ticks, format } = distanceTicks(xMax);
  return { ...series, xMax, xTicks: ticks, formatX: format };
}

/**
 * Held flat out to both ends of the track: an average over a stretch is drawn at its middle, but
 * stands for the whole of it, so without this the line starts half a stretch in.
 */
export function toEnds(points: ChartPoint[], length: number): ChartPoint[] {
  if (points.length === 0) return points;
  const first = points[0];
  const last = points[points.length - 1];
  return [
    ...(first.x > 0 ? [{ ...first, x: 0 }] : []),
    ...points,
    ...(last.x < length ? [{ ...last, x: length }] : []),
  ];
}

function tabSeries(
  tab: ActivityTab,
  activity: RecordingActivity,
  pref: MeasurementPrefUtility,
  t: Translate
): Omit<ActivitySeries, 'xMax' | 'xTicks' | 'formatX'> | null {
  const x = (meters: number) => meters / pref.metresFromBigUnit(1);
  const length = x(activity.totalMeters);
  const height = (meters: number) => toSmallUnit(meters, pref);
  const whole = (y: number) => String(Math.round(y));
  const profile = activity.elevation?.samples;
  const backdrop = profile ? toEnds(profile.map((s) => ({ x: x(s.distance), y: height(s.value) })), length) : null;

  switch (tab) {
    case 'elevation': {
      const samples = activity.elevation?.samples;
      if (!samples) return null;
      const points = toEnds(smoothed(samples.map((s) => ({ x: x(s.distance), y: height(s.value) }))), length);
      const values = points.map((p) => p.y);
      const range = yRange(values, height(MINIMUM_ELEVATION_SPREAD_METERS));
      const ticks = elevationTicks(range.yMin ?? Math.min(...values), range.yMax ?? Math.max(...values));
      return {
        points,
        colors: { color: ELEVATION_COLOR },
        reverse: false,
        yMin: ticks[0],
        yMax: ticks[ticks.length - 1],
        yTicks: ticks,
        formatValue: whole,
        unit: pref.meterSymbol(),
        backdrop: null,
      };
    }
    case 'ascent': {
      const samples = activity.ascent?.samples;
      if (!samples) return null;
      // Already read over 100 m; smoothing again would draw the steepest pitch below its own max gradient.
      const gradients = samples.map((s) => ({ x: x(s.distance), y: s.gradient }));
      const speeds = smoothedReadings(samples.map((s) => s.verticalSpeed));
      const points = toEnds(gradients.map((p, i) => ({ ...p, speed: speeds[i] })), length);
      const scales = ascentSpeedScales(points);
      const values = points.map((p) => p.y);
      const ticks = gradientTicks(Math.min(...values), Math.max(...values));
      return {
        points,
        colors: { stops: points.map((p) => ({ x: p.x, color: ascentColor(p, scales) })) },
        reverse: false,
        yMin: ticks[0],
        yMax: ticks[ticks.length - 1],
        yTicks: ticks,
        formatValue: whole,
        unit: '%',
        valueLabel: t('recordings.activity.gradient'),
        baseline: true,
        speedScales: scales,
        backdrop,
      };
    }
    case 'heartRate': {
      const samples = activity.heartRate?.samples;
      if (!samples) return null;
      const points = smoothed(samples.map((s) => ({ x: x(s.distance), y: s.value })));
      const values = points.map((p) => p.y);
      return {
        points,
        colors: heartRateScale(values)!,
        reverse: false,
        ...yRange(values, MINIMUM_HEART_RATE_SPREAD_BPM),
        formatValue: whole,
        unit: t('recordings.activity.bpm'),
        backdrop,
      };
    }
    case 'steps': {
      const samples = activity.steps?.samples;
      if (!samples) return null;
      const points = toEnds(smoothed(samples.map((s) => ({ x: x(s.distance), y: s.value }))), length);
      const values = points.map((p) => p.y);
      return {
        points,
        colors: cadenceScale(values)!,
        reverse: false,
        ...yRange(values, MINIMUM_CADENCE_SPREAD_STEPS_PER_MINUTE),
        formatValue: whole,
        unit: t('recordings.activity.spm'),
        backdrop,
      };
    }
    case 'pace': {
      const samples = activity.pace?.samples;
      if (!samples) return null;
      const perUnit = secondsPerBigUnit(1, pref);
      const perMeter = toEnds(smoothed(samples.map((s) => ({ x: x(s.distance), y: s.value }))), length);
      const points = perMeter.map((p) => ({ x: p.x, y: p.y * perUnit }));
      const values = points.map((p) => p.y);
      const range = yRange(values, MINIMUM_PACE_SPREAD_SECONDS_PER_METER * perUnit);
      const lowest = range.yMin ?? Math.min(...values);
      const highest = range.yMax ?? Math.max(...values);
      const pad = (highest - lowest) * 0.04;
      return {
        points,
        colors: scaled(paceScale(perMeter.map((p) => p.y))!, perUnit),
        reverse: true,
        yMin: lowest - pad,
        yMax: highest + pad,
        yTicks: paceTicks(lowest - pad, highest + pad),
        formatValue: minutesSeconds,
        unit: paceUnit(pref),
        backdrop,
      };
    }
  }
}

export interface ActivityTooltip {
  title: string;
  lines: string[];
}

export interface BackdropHighPoint {
  x: number;
  y: number;
  label: string;
  value: string;
}

/** The top of the grey elevation behind a graph, labelled; null without one or on a flat walk. */
export function backdropHighPoint(
  series: ActivitySeries | null,
  pref: MeasurementPrefUtility,
  t: Translate,
  locale?: string
): BackdropHighPoint | null {
  const index = highPointIndex(series?.backdrop);
  if (index === null || !series?.backdrop) return null;
  const { x, y } = series.backdrop[index];
  return { x, y, label: t('recordings.activity.high_point'), value: withUnit(wholeNumber(y, locale), pref.meterSymbol()) };
}

/** "3.2 km · 1:02:15", then the reading and, behind it, the height there. */
export function activityTooltip(
  point: ChartPoint,
  series: ActivitySeries,
  activity: RecordingActivity,
  pref: MeasurementPrefUtility,
  t: Translate
): ActivityTooltip {
  const elapsedMs = activity.elapsedMsAt(pref.metresFromBigUnit(point.x));
  const distance = `${point.x.toFixed(1)} ${pref.kilometerSymbol()}`;
  const value = withUnit(series.formatValue(point.y), series.unit);
  const lines = [series.valueLabel ? `${series.valueLabel}: ${value}` : value];
  if (point.speed != null && Math.abs(point.y) >= CLIMBING_GRADIENT_PERCENT) {
    const rate = withUnit(verticalSpeedValue(Math.abs(point.speed), pref), verticalSpeedUnit(pref));
    lines.push(point.y > 0 ? t('recordings.activity.climbing_at', { rate }) : t('recordings.activity.descending_at', { rate }));
  }
  if (series.backdrop) {
    const height = valueAt(point.x, series.backdrop.map((p) => p.x), series.backdrop.map((p) => p.y));
    lines.push(`${t('recordings.activity.elevation')}: ${Math.round(height)} ${pref.meterSymbol()}`);
  }
  return { title: elapsedMs === null ? distance : `${distance} · ${durationLabel(elapsedMs / 1000)}`, lines };
}

export interface AscentInsight {
  /** "Fastest climbing" with times, "Steepest section" without. */
  fastest: boolean;
  rate: string | null;
  range: string;
  gradient: string;
}

/** "km 3.1–3.4", the highlight's stretch in the user's big unit. */
export function ascentInsight(activity: RecordingActivity, pref: MeasurementPrefUtility, locale?: string): AscentInsight | null {
  const highlight = activity.ascent?.highlight;
  if (!highlight) return null;
  const at = (meters: number) => (meters / pref.metresFromBigUnit(1)).toFixed(1);
  return {
    fastest: highlight.metersPerHour !== null,
    rate: highlight.metersPerHour === null
      ? null
      : withUnit(verticalSpeedValue(highlight.metersPerHour, pref, locale), verticalSpeedUnit(pref)),
    range: `${pref.kilometerSymbol()} ${at(highlight.from)}–${at(highlight.to)}`,
    gradient: `${Math.round(highlight.gradient)}%`,
  };
}

export interface AscentLegendRow {
  label: string;
  range: string;
  colors: string[];
}

/** The speeds ascent's colours run between, slow to fast; empty without times. */
export function ascentLegend(
  series: ActivitySeries,
  pref: MeasurementPrefUtility,
  t: Translate,
  locale?: string
): AscentLegendRow[] {
  const scales = series.speedScales;
  if (!scales) return [];
  const range = ({ low, high }: SpeedScale) =>
    withUnit(`${verticalSpeedValue(low, pref, locale)}–${verticalSpeedValue(high, pref, locale)}`, verticalSpeedUnit(pref));
  return [
    ...(scales.climbing ? [{ label: t('recordings.activity.climbing_speed'), range: range(scales.climbing), colors: CLIMBING_SPEED_COLORS }] : []),
    ...(scales.descending ? [{ label: t('recordings.activity.descending_speed'), range: range(scales.descending), colors: DESCENDING_SPEED_COLORS }] : []),
  ];
}
